/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Page, Card, Text, BlockStack, Banner, Grid } from "@shopify/polaris";
import { AlertTriangleIcon } from "@shopify/polaris-icons";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useFetchCourierAnalyticsQuery } from "../store/store/slices/DashboardRTK";
import DateRange from "../components/atoms/DateRange";
import dayjs from "dayjs";

// Shopify Polaris color palette
const POLARIS_COLORS = {
  primary: "#008060", // Success/Primary green
  info: "#5c6ac4", // Info blue
  critical: "#d72c0d", // Critical red
  warning: "#ffc453", // Warning yellow
  subduedBg: "#f6f6f7", // Subdued background
  border: "#e1e3e5", // Border color
  textSubdued: "#6d7175", // Subdued text
  surface: "#ffffff", // Surface white
};

// Courier colors - lightish pastel shades
const COURIER_COLORS: { [key: string]: string } = {
  LEOPARD: "#ADD8E6", // Light blue
  MNP: "#B0E0E6", // Powder blue
  TCS: "#AFEEEE", // Pale turquoise
  POSTEX: "#FFE4B5", // Light yellow (moccasin)
};

const CourierOrderAnalytics = () => {
  // Get date range from URL params or use default (last 30 days)
  // Use useLocation to make it reactive to URL changes
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const startDate = params.get("bookingfrom")
    ? dayjs(new Date(params.get("bookingfrom")!)).format("YYYY-MM-DD")
    : dayjs().subtract(29, "day").format("YYYY-MM-DD");
  const endDate = params.get("bookingto")
    ? dayjs(new Date(params.get("bookingto")!)).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  const {
    data: analytics,
    isLoading,
    isError,
    error,
    isFetching
  } = useFetchCourierAnalyticsQuery({
    startDate,
    endDate,
  });

  // Format courier data for bar chart (Number of bookings)
  const courierBookingsData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics)
      .map(([name, data]: [string, any]) => ({
        name,
        bookings: data.total || 0,
      }))
      .sort((a, b) => b.bookings - a.bookings);
  }, [analytics]);

  // Format delivery ratio data
  const deliveryRatioData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics)
      .map(([name, data]: [string, any]) => {
        const total = data.total || 0;
        const delivered = data.DELIVERED || 0;
        const ratio = total > 0 ? (delivered / total) * 100 : 0;
        return {
          name,
          ratio: ratio.toFixed(1),
          total,
        };
      })
      .sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));
  }, [analytics]);

  // Format average delivery time data
  const avgDeliveryTimeData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics)
      .map(([name, data]: [string, any]) => ({
        name,
        days: data.avgDeliveryDays || 0,
        total: data.total || 0,
      }))
      .sort((a, b) => b.days - a.days);
  }, [analytics]);

  // Format RTS ratio data
  const rtsRatioData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics)
      .map(([name, data]: [string, any]) => {
        const total = data.total || 0;
        const rts = data.RTS || 0;
        const ratio = total > 0 ? (rts / total) * 100 : 0;
        return {
          name,
          ratio: ratio.toFixed(1),
          total,
        };
      })
      .sort((a, b) => parseFloat(b.ratio) - parseFloat(a.ratio));
  }, [analytics]);

  if (isError) {
    return (
      <Page title="Courier Analytics">
        <Banner icon={AlertTriangleIcon} tone="warning">
          {(error as any)?.data?.errors?.[0]?.msg || "Failed to load courier analytics"}
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Courier Analytics" titleHidden>
      <div style={{ padding: "20px 0" }}>
        {/* Date Range Filter */}
        <div style={{ marginBottom: "24px" }}>
          <DateRange />
        </div>

        <BlockStack gap="400">
          {/* First Row - 2 Cards */}
          <Grid columns={{ xs: 1, md: 2, lg: 2, xl: 2 }}>
            {/* Card 1: Number of bookings with bar chart */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "350px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "20px",
                            width: "200px",
                            marginBottom: "16px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "300px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Number of bookings
                        </Text>
                        {analytics && Object.keys(analytics).length > 0 ? (
                          <div style={{ height: "300px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart
                                data={courierBookingsData}
                                margin={{ top: 5, right: 30, left: 5, bottom: 5 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke={POLARIS_COLORS.border}
                                />
                                <XAxis
                                  dataKey="name"
                                  tick={{
                                    fill: POLARIS_COLORS.textSubdued,
                                    fontSize: 12,
                                  }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <YAxis
                                  tick={{
                                    fill: POLARIS_COLORS.textSubdued,
                                    fontSize: 12,
                                  }}
                                  axisLine={{ stroke: POLARIS_COLORS.border }}
                                />
                                <Tooltip
                                  formatter={(value: number) => [
                                    `${value.toLocaleString()} bookings`,
                                    "Bookings",
                                  ]}
                                  contentStyle={{
                                    backgroundColor: POLARIS_COLORS.surface,
                                    border: `1px solid ${POLARIS_COLORS.border}`,
                                    borderRadius: "6px",
                                    padding: "8px 12px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                                  }}
                                />
                                <Bar
                                  dataKey="bookings"
                                  radius={[4, 4, 0, 0]}
                                  label={{
                                    position: "top",
                                    fill: POLARIS_COLORS.textSubdued,
                                    fontSize: 12,
                                    formatter: (value: number) =>
                                      value.toLocaleString(),
                                  }}
                                >
                                  {courierBookingsData.map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={
                                        COURIER_COLORS[entry.name] ||
                                        POLARIS_COLORS.info
                                      }
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div
                            style={{
                              height: "300px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Text tone="subdued" as="p">
                              No data available
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Card 2: Delivery ratio table */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "350px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "20px",
                            width: "150px",
                            marginBottom: "16px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "300px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Delivery ratio
                        </Text>
                        {analytics && Object.keys(analytics).length > 0 ? (
                          <BlockStack gap="0">
                            {deliveryRatioData.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "12px 0",
                                  borderBottom:
                                    index < deliveryRatioData.length - 1
                                      ? `1px solid ${POLARIS_COLORS.border}`
                                      : "none",
                                }}
                              >
                                <Text variant="bodyMd" as="span">
                                  {item.name}
                                </Text>
                                <Text variant="bodyMd" fontWeight="semibold" as="span">
                                  {item.ratio}%
                                </Text>
                              </div>
                            ))}
                          </BlockStack>
                        ) : (
                          <div
                            style={{
                              height: "300px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Text tone="subdued" as="p">
                              No data available
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>

          {/* Second Row - 2 Cards */}
          <Grid columns={{ xs: 1, md: 2, lg: 2, xl: 2 }}>
            {/* Card 3: Avg delivery time table */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "350px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "20px",
                            width: "180px",
                            marginBottom: "16px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "300px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          Avg delivery time
                        </Text>
                        {analytics && Object.keys(analytics).length > 0 ? (
                          <BlockStack gap="0">
                            {avgDeliveryTimeData.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "12px 0",
                                  borderBottom:
                                    index < avgDeliveryTimeData.length - 1
                                      ? `1px solid ${POLARIS_COLORS.border}`
                                      : "none",
                                }}
                              >
                                <Text variant="bodyMd" as="span">
                                  {item.name}
                                </Text>
                                <Text variant="bodyMd" fontWeight="semibold" as="span">
                                  {item.days.toFixed(1)} {item.days === 1 ? "day" : "days"}
                                </Text>
                              </div>
                            ))}
                          </BlockStack>
                        ) : (
                          <div
                            style={{
                              height: "300px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Text tone="subdued" as="p">
                              No data available
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>

            {/* Card 4: RTS ratio table */}
            <Grid.Cell>
              <Card>
                <div style={{ minHeight: "350px" }}>
                  <BlockStack gap="400">
                    {isLoading || isFetching ? (
                      <div className="dashboard-skeleton-wrapper">
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "20px",
                            width: "120px",
                            marginBottom: "16px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                        <div
                          className="dashboard-skeleton"
                          style={{
                            height: "300px",
                            borderRadius: "4px",
                            backgroundColor: POLARIS_COLORS.subduedBg,
                          }}
                        />
                      </div>
                    ) : (
                      <>
                        <Text variant="headingMd" as="h3">
                          RTS ratio
                        </Text>
                        {analytics && Object.keys(analytics).length > 0 ? (
                          <BlockStack gap="0">
                            {rtsRatioData.map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "12px 0",
                                  borderBottom:
                                    index < rtsRatioData.length - 1
                                      ? `1px solid ${POLARIS_COLORS.border}`
                                      : "none",
                                }}
                              >
                                <Text variant="bodyMd" as="span">
                                  {item.name}
                                </Text>
                                <Text variant="bodyMd" fontWeight="semibold" as="span">
                                  {item.ratio}%
                                </Text>
                              </div>
                            ))}
                          </BlockStack>
                        ) : (
                          <div
                            style={{
                              height: "300px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Text tone="subdued" as="p">
                              No data available
                            </Text>
                          </div>
                        )}
                      </>
                    )}
                  </BlockStack>
                </div>
              </Card>
            </Grid.Cell>
          </Grid>
        </BlockStack>
    </div>
    </Page>
  );
};

export default CourierOrderAnalytics;
